import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import DetailLearnMore from './Components/DetailLearnMore';
import DetailDescription from './Components/DetailDescription';
import {
  Button,
  EventTimeAndLocationDetails,
  FallbackHeaderImage,
  imageUrl,
  imageVersionFor,
  useBrandColors,
  LEGACY_UNBRANDED_FORM_COLOR,
} from '@charitystack/shared';
import { getGlobalImageUrl } from './config/env';
import { useIsHostedPage } from './hooks/useIsHostedPage';
// formHeightUtils no longer needed — detail pages use CSS container-query heights
import { getPageTransitionClasses } from './Util/pageTransition';

function EventDetailPageContent({
  data,
  setPage,
  brandColors: propBrandColors,
  generatedBrandColors: parentGeneratedBrandColors,
  isDrawerOpen,
  hostedPage,
  transitionToPage,
  previewMode = false,
  isPageActive = false,
  pageTransitionDirection = null,
  formDisplayMode,
}) {
  const isHostedPage = useIsHostedPage();

  // NEW: Use shared brand colors hook - direct color management
  const [lightColor, setLightColor] = useState(data?.lightColor || LEGACY_UNBRANDED_FORM_COLOR); // CON-528: no colour is no brand, never sage
  const { brandColors: localBrandColors, primaryColor: localPrimaryColor, darkColor: localDarkColor } = useBrandColors(lightColor);

  // CRITICAL FIX: Use pre-generated brand colors from parent (EventForm) if available
  // This prevents flicker caused by useBrandColors initializing with empty state
  const hasParentBrandColors = parentGeneratedBrandColors && Object.keys(parentGeneratedBrandColors).length > 0;
  const brandColors = hasParentBrandColors ? parentGeneratedBrandColors : localBrandColors;
  const primaryColor = hasParentBrandColors ? (parentGeneratedBrandColors['accent-50'] || localPrimaryColor) : localPrimaryColor;
  const darkColor = hasParentBrandColors ? (parentGeneratedBrandColors['accent-60'] || localDarkColor) : localDarkColor;
  const [primaryImageError, setPrimaryImageError] = useState(false);
  const [secondaryImageError, setSecondaryImageError] = useState(false);
  const persistedImageUrl = imageUrl({
    base: getGlobalImageUrl(),
    type: data.elementType === 'EVENT' ? 'event' : 'fundraiser',
    name: `${data.nonprofitName}_${data.formID}`,
    version: imageVersionFor(data),
  });
  
  // Extract brand color for dynamic SVG fallback
  const brandColor = primaryColor || lightColor || data?.lightColor;
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  // The card never grows: the full description and the FAQs open in a sheet over it
  // (DetailLearnMore), so it always keeps its fixed height and ends on Get tickets.
  const bodyRef = useRef(null);
  const descriptionSlotRef = useRef(null);
  const collapsedDescriptionSlotPxRef = useRef(null);
  // Detail pages use CSS container-query-based fixed height (dynamic-detail-height class)
  const useFixedHeight = !isDrawerOpen;
  // Keep column flex on the frosted shell while expanded so closing FAQ does not
  // toggle layout mode (block → flex), which reflowed the white card and made the
  // Support/FAQs footer jump. Only gate overflow:hidden to collapsed so expanded
  // content can take its natural height without resizing the shell frame-by-frame.
  const fixedDetailColumnStyles = useFixedHeight
    ? { display: 'flex', flexDirection: 'column' }
    : {};
  const fixedDetailOverflowHidden = useFixedHeight
    ? { overflow: 'hidden' }
    : {};
  const fixedDetailBodyStyles = useFixedHeight
    ? { flex: '1 1 auto', minHeight: 0 }
    : {};
  const transitionClassName = getPageTransitionClasses(isPageActive, pageTransitionDirection, formDisplayMode);
  const transitionWrapperStyle =
    useFixedHeight || isDrawerOpen
      ? {
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
        }
      : undefined;

  // Detect if the body content overflows the fixed-height container.
  // Skip re-measurement both when expanded AND on the collapse transition,
  // because the container hasn't settled to its constrained height yet during
  // collapse — scrollHeight === clientHeight would incorrectly report no overflow.
  useLayoutEffect(() => {
    if (!bodyRef.current || isDrawerOpen) return;
    const el = bodyRef.current;
    setIsDescriptionOverflowing(el.scrollHeight > el.clientHeight);
  }, [data?.eventDetails, data?.enableTimeAndLocation, isDrawerOpen]);

  useLayoutEffect(() => {
    if (!descriptionSlotRef.current || isDrawerOpen) return;
    collapsedDescriptionSlotPxRef.current = descriptionSlotRef.current.clientHeight;
  }, [
    isDrawerOpen,
    isDescriptionOverflowing,
    data?.eventDetails,
    data?.enableTimeAndLocation,
  ]);

  // Same as EmbedFormDetailPage: keep slot lock for any clipped description so
  // a clipped description keeps its slot height and the HTML body does not jump from the top.
  const descriptionSlotLockPx = (() => {
    if (
      !useFixedHeight ||
      isDrawerOpen ||
      !isDescriptionOverflowing
    ) {
      return null;
    }
    const px =
      collapsedDescriptionSlotPxRef.current ?? bodyRef.current?.clientHeight ?? null;
    return px != null && px > 0 ? px : null;
  })();

  useEffect(() => {
    if (data?.lightColor) {
      setLightColor(data.lightColor); // Direct color extraction
    }
    // propBrandColors handling removed - useBrandColors hook handles all color generation
  }, [data?.lightColor]);

  // Reset error states when image changes in preview mode
  useEffect(() => {
    if (previewMode && data?.image !== undefined) {
      setPrimaryImageError(false);
      setSecondaryImageError(false);
    }
  }, [previewMode, data?.image]);

  // The retry below only helps if it asks for a different URL than the load that
  // failed. Outside preview both are the same stable versioned URL, so React keeps
  // the same <img>, nothing is re-requested, no second error fires, and an event
  // with no photo would show a broken image instead of the brand fallback. An
  // identical retry therefore counts as already failed.
  const primaryImageSrc = previewMode ? data?.image : persistedImageUrl;
  const retryRepeatsFailedUrl = persistedImageUrl === primaryImageSrc;
  const imageFailed = primaryImageError && (secondaryImageError || retryRepeatsFailedUrl);

  // Add defensive programming
  if (!data) {
    return <div className="text-center text-[color:var(--cs-text-muted)]">Loading...</div>;
  }

  if (!data.nonprofitName || !data.formName) {
    return <div className="text-center text-[color:var(--cs-text-muted)]">Invalid data received</div>;
  }

  const handleContinue = () => {
    setPage('FORM', 'forward');
  };

  return (
    <div
      className={`${isDrawerOpen ? '' : 'container'} ${isDrawerOpen ? '' : 'mx-auto'} h-full items-center ${
        isDrawerOpen ? 'w-full px-1' : 'max-w-md'
      }`}
      style={{
        minHeight: isDrawerOpen ? '100%' : 'fit-content',
        height: isDrawerOpen ? '100%' : 'auto',
      }}
    >
      <div
        className={isHostedPage && !isDrawerOpen ? 'hosted-spacing-wrapper' : !isHostedPage && !isDrawerOpen ? 'dynamic-spacing-wrapper' : ''}
        style={{
          ...(!isHostedPage || isDrawerOpen ? { padding: isDrawerOpen ? '16px 0' : '24px 0' } : {}),
          margin: '0',
          height: isDrawerOpen ? '100%' : useFixedHeight ? 'auto' : 'fit-content',
          boxSizing: 'border-box',
        }}
      >
        <div
          // The FAQs sheet (DetailMoreRow) opens over this card: it is the drawer's scope.
          data-pane-sheet-scope=""
          className={`relative overflow-hidden ${!isDrawerOpen ? 'frosted-background cs-form-card' : ''} ${!isDrawerOpen && !isHostedPage ? 'dynamic-frosted-margin' : ''} ${useFixedHeight ? 'dynamic-detail-height event-detail-height' : ''}`}
          style={{
            ...fixedDetailColumnStyles,
            ...fixedDetailOverflowHidden,
            ...(isDrawerOpen && {
              margin: '0',
              padding: '10px',
              borderRadius: '0',
            }),
          }}
        >
          {/* Header Image Container - No Padding */}
          {/* Flush to the card's edge, top corners on its inner curve (Paper 7VQ-0). */}
          <div
            className={`shrink-0 overflow-hidden bg-[color:var(--cs-surface-0)] ${isDrawerOpen ? 'rounded-none' : 'cs-form-card-media'}`}
          >
            {/* Show fallback immediately if in preview mode and no image */}
            {previewMode && !data.image ? (
              <div className="aspect-video w-full relative">
                <FallbackHeaderImage
                  alt="Default fundraiser image: heart-shaped forest"
                  variant="donation-elements"
                  brandColor={brandColor}
                  title={data.formName}
                />
              </div>
            ) : !primaryImageError ? (
              <div className="aspect-video w-full relative">
                <img
                  src={
                    previewMode
                      ? data.image
                      : persistedImageUrl
                  }
                  className="absolute inset-0 w-full h-full object-cover bg-[color:var(--cs-placeholder)]"
                  alt="Event Header"
                  onError={() => setPrimaryImageError(true)}
                  loading="eager"
                  key={previewMode ? data.image : undefined} // Force re-render in preview mode
                />
              </div>
            ) : !imageFailed ? (
              <div className="aspect-video w-full relative">
                <img
                  src={persistedImageUrl}
                  className="absolute inset-0 w-full h-full object-cover bg-[color:var(--cs-placeholder)]"
                  alt="Event Header"
                  onError={() => setSecondaryImageError(true)}
                  loading="eager"
                />
              </div>
            ) : (
              <div className="aspect-video w-full relative">
                <FallbackHeaderImage
                  alt="Default fundraiser image: heart-shaped forest"
                  variant="donation-elements"
                  brandColor={brandColor}
                  title={data.formName}
                />
              </div>
            )}
          </div>

          {/* Content Container - Normal Padding */}
          <div
            className={`bg-white flex flex-col ${
              isDrawerOpen
                ? 'h-full rounded-none'
                : 'cs-form-card-body'
            }`}
            style={{
              ...(isDrawerOpen ? { padding: '0' } : {}),
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <div className={transitionClassName} style={transitionWrapperStyle}>
              <div
                className="detail-description-clip-area min-h-0 flex-1 flex flex-col"
              >
                <div
                  ref={descriptionSlotRef}
                  style={{
                    position: 'relative',
                    ...(descriptionSlotLockPx != null
                      ? {
                          flex: '0 0 auto',
                          height: descriptionSlotLockPx,
                          maxHeight: descriptionSlotLockPx,
                          minHeight: 0,
                          overflow: 'hidden',
                        }
                      : { flex: '1 1 auto', minHeight: 0 }),
                  }}
                >
                  <div
                    ref={bodyRef}
                    style={{
                      ...fixedDetailBodyStyles,
                      overflow: 'hidden',
                      height: '100%',
                    }}
                  >
                    {/* Event Title */}
                    <div>
                      <h1 className="m-0 font-sans text-[length:var(--cs-text-2xl)] leading-[var(--cs-leading-30)] font-semibold tracking-[var(--cs-tracking-tight)] text-[color:var(--cs-text-primary)]">{data.formName}</h1>
                    </div>

                    {/* Event Time & Location Details — above description */}
                    {data.enableTimeAndLocation && (
                      <div className="mt-[var(--cs-space-4)]">
                        <EventTimeAndLocationDetails
                          eventStartDate={data.eventStartDate || data.eventDate}
                          eventEndDate={data.eventEndDate}
                          eventStartTime={data.eventStartTime}
                          eventEndTime={data.eventEndTime}
                          eventLocation={data.eventLocation}
                          timeZone={data.timeZone}
                          showMap={false}
                        />
                      </div>
                    )}

                    {/* Event Description (DetailDescription: the same render as the Learn more sheet) */}
                    <DetailDescription html={data.eventDetails} className="mt-[var(--cs-space-4)]" />
                  </div>
                  {/* Fade gradient to mask half-cut lines when description overflows */}
                  {isDescriptionOverflowing && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '24px',
                        background: 'linear-gradient(to top, white, transparent)',
                        pointerEvents: 'none',
                      }}
                      />
                  )}
                </div>

                {/* Learn more (full description + FAQs in a sheet) or FAQs, above Get tickets (DetailLearnMore) */}
                {!isDrawerOpen && (
                  <div
                    className="flex-shrink-0"
                    style={{ marginTop: '0.25rem' }}
                  >
                    <DetailLearnMore
                      title={data.formName}
                      descriptionHtml={data.eventDetails}
                      isDescriptionClipped={isDescriptionOverflowing}
                      faqs={data.formFaqs}
                      elementID={data.elementID}
                    />
                  </div>
                )}
              </div>

              {/* Get tickets — the card ends on its primary button; FAQs sit on the Read more row above */}
              <div className="flex-shrink-0 flex flex-col w-full gap-2 pt-[var(--cs-space-3)]">
                <Button
                  type="button"
                  // The primary CTA (CON-514): brand role fill, 48 tall.
                  family="cta"
                  variant="cta"
                  size="cta"
                  onClick={handleContinue}
                >
                  <span className="dynamic-button-text flex items-center justify-center gap-2">
                    Get tickets
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventDetailPage({
  data,
  setPage,
  brandColors: propBrandColors,
  generatedBrandColors,
  isDrawerOpen,
  hostedPage,
  elementID,
  domID,
  transitionToPage, // New prop for smooth transitions
  previewMode = false,
  isPageActive = false,
  pageTransitionDirection = null,
  formDisplayMode,
}) {
  return (
    <EventDetailPageContent
      data={data}
      setPage={setPage}
      brandColors={propBrandColors}
      generatedBrandColors={generatedBrandColors}
      isDrawerOpen={isDrawerOpen}
      hostedPage={hostedPage}
      transitionToPage={transitionToPage}
      previewMode={previewMode}
      isPageActive={isPageActive}
      pageTransitionDirection={pageTransitionDirection}
      formDisplayMode={formDisplayMode}
    />
  );
}

export default EventDetailPage;
